import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router'
import { navGroupsFor, isGroup } from './tenants'
import { cx } from '../ui'

/**
 * How long the flyout waits before closing after the pointer leaves.
 *
 * The path from a group header to its panel goes diagonally across the gap between them, and a
 * flyout that closes the instant the pointer leaves the header snatches the panel away mid-
 * movement. The delay is the cheap version of tracking that triangle: long enough to cross,
 * short enough that a panel never feels stuck open.
 */
const CLOSE_DELAY_MS = 180

/**
 * Below this the rail is not a rail — it is a full-width block above the content, and a panel
 * opening at its right edge would open off the screen.
 */
const FLYOUT_MIN_WIDTH = '(min-width: 901px)'

function useFlyoutLayout() {
  const [enabled, setEnabled] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(FLYOUT_MIN_WIDTH).matches,
  )

  useEffect(() => {
    const query = window.matchMedia(FLYOUT_MIN_WIDTH)
    const sync = () => setEnabled(query.matches)

    sync()
    query.addEventListener('change', sync)
    return () => query.removeEventListener('change', sync)
  }, [])

  return enabled
}

/**
 * The 256px sidebar.
 *
 * TWO BEHAVIOURS, because the rail has two shapes. Wide, groups open as a flyout beside their
 * header — the pattern asked for, and the one that keeps nine destinations reachable without a
 * column of permanently open lists. Narrow, the rail stacks full-width above the content, a
 * flyout would open past the right edge of the screen, and the groups stay an in-place accordion
 * instead. Same markup, same links, different opening.
 *
 * What the flyout costs, stated rather than discovered later: a closed group's links are not in
 * the document, so the browser's own find-in-page cannot reach them. That is inherent to the
 * pattern — a panel that is always in the DOM is not a flyout — and it is why the accordion
 * keeps its hidden-but-mounted list at narrow widths, where nothing is gained by hiding them.
 */
export function SideNav({ assessesAzure = false, assessesAi = false }: { assessesAzure?: boolean; assessesAi?: boolean }) {
  const flyout = useFlyoutLayout()

  // Which group is showing its panel. One at a time: two open flyouts would overlap.
  const [openGroup, setOpenGroup] = useState<string | null>(null)

  // Accordion state, kept separate from the flyout's. They are different questions -- "which one
  // is showing" and "which ones has this person folded away" -- and sharing one variable made
  // resizing the window silently collapse groups the reader had opened.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const closeTimer = useRef<number | undefined>(undefined)
  const navRef = useRef<HTMLElement | null>(null)

  // Set for the one focus event Escape itself causes, so handing focus back to the group's header
  // does not re-open the panel Escape just closed (the header opens its panel on focus).
  const refocusing = useRef(false)

  const cancelClose = useCallback(() => {
    window.clearTimeout(closeTimer.current)
  }, [])

  const scheduleClose = useCallback(() => {
    window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setOpenGroup(null), CLOSE_DELAY_MS)
  }, [])

  useEffect(() => () => window.clearTimeout(closeTimer.current), [])

  // A flyout that only closes on hover-out strands keyboard and touch users with a panel over
  // the content. Escape and a click elsewhere are the two exits people already expect.
  useEffect(() => {
    if (!flyout || openGroup === null) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Hiding the panel that holds focus used to drop focus onto the page body. Hand it back to
      // the header that opened the panel.
      const open = navRef.current?.querySelector<HTMLElement>('.nav-group.is-open')
      if (open?.contains(document.activeElement)) {
        refocusing.current = true
        open.querySelector<HTMLButtonElement>('.nav-group-header')?.focus()
        refocusing.current = false
      }
      setOpenGroup(null)
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!navRef.current?.contains(event.target as Node)) setOpenGroup(null)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [flyout, openGroup])

  // Leaving flyout mode must not leave a panel pinned open over the stacked layout.
  useEffect(() => {
    if (!flyout) setOpenGroup(null)
  }, [flyout])

  return (
    <nav
      className={cx('side-nav', flyout && 'is-flyout')}
      aria-label="Sections"
      ref={navRef}
    >
      {navGroupsFor(assessesAzure, assessesAi).map((entry) => {
        // A destination goes straight in the rail. No chevron, no panel, nothing to open --
        // the two that used to be groups were a heading you had to hover to reach one link.
        if (!isGroup(entry)) {
          return (
            <NavLink
              key={entry.path}
              to={entry.path}
              className={({ isActive }) => cx('nav-item', 'nav-item-top', isActive && 'is-active')}
              onClick={() => setOpenGroup(null)}
              // Moving onto a plain destination closes whatever panel was open, or the panel
              // hangs over the content while the pointer is somewhere else entirely.
              onMouseEnter={flyout ? () => { cancelClose(); setOpenGroup(null) } : undefined}
            >
              {entry.label}
            </NavLink>
          )
        }

        const group = entry
        const isOpen = flyout
          ? openGroup === group.label
          : !(collapsed[group.label] ?? false)

        const panelId = `nav-group-${group.label.toLowerCase().replace(/\W+/g, '-')}`

        return (
          <div
            className={cx('nav-group', isOpen && 'is-open')}
            key={group.label}
            onMouseEnter={flyout ? () => { cancelClose(); setOpenGroup(group.label) } : undefined}
            onMouseLeave={flyout ? scheduleClose : undefined}
            // Focus opens the panel, so focus leaving must close it. Without this, tabbing past
            // a group left its panel laid over the page content with nothing pointing at it.
            onBlur={
              flyout
                ? (event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                      setOpenGroup((current) => (current === group.label ? null : current))
                    }
                  }
                : undefined
            }
          >
            <button
              type="button"
              className="nav-group-header"
              aria-expanded={isOpen}
              aria-controls={panelId}
              // Announced as a menu only where it behaves like one. In accordion mode this is a
              // disclosure, and claiming a popup that never appears is worse than claiming none.
              aria-haspopup={flyout ? 'true' : undefined}
              onClick={() =>
                flyout
                  ? setOpenGroup(isOpen ? null : group.label)
                  : setCollapsed((current) => ({
                      ...current,
                      [group.label]: isOpen,
                    }))
              }
              onFocus={
                flyout
                  ? () => {
                      if (refocusing.current) return
                      cancelClose()
                      setOpenGroup(group.label)
                    }
                  : undefined
              }
            >
              <svg
                className={cx('nav-chevron', !isOpen && 'is-collapsed')}
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
              {group.label}
            </button>

            {/* Hidden rather than unmounted in accordion mode, so a folded group's links stay
                findable by the browser's own in-page search. In flyout mode the panel is
                positioned out of flow and hidden is what keeps it out of the tab order. */}
            <div className="nav-panel" id={panelId} hidden={!isOpen}>
              {flyout ? <div className="nav-panel-title">{group.label}</div> : null}

              <ul className="nav-items">
                {group.items.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) => cx('nav-item', isActive && 'is-active')}
                      onClick={() => setOpenGroup(null)}
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )
      })}
    </nav>
  )
}
